import React, { useState, useEffect, useCallback } from 'react';
import { FiGift, FiPlus, FiShuffle } from 'react-icons/fi';
import api from '../services/api';
import './Admin.css';

function AdminPrizeManagementPage({ campaignId }) {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const emptyPrize = {
    name: '', displayName: '', icon: '', color: '#FFD700',
    weight: 1, enabled: true, neverShow: false,
    totalStock: 0, sortOrder: 0, prizeType: 'physical',
    claimInstructions: '',
  };

  const [form, setForm] = useState(emptyPrize);

  const loadPrizes = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const data = await api.getPrizes(campaignId);
      setPrizes(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { loadPrizes(); }, [loadPrizes]);

  const handleEdit = (prize) => {
    setEditing(prize.id);
    setForm({
      name: prize.name,
      displayName: prize.display_name,
      icon: prize.icon || '',
      color: prize.color || '#FFD700',
      weight: prize.weight,
      enabled: prize.enabled,
      neverShow: prize.never_show,
      totalStock: prize.total_stock,
      sortOrder: prize.sort_order,
      prizeType: prize.prize_type,
      claimInstructions: prize.claim_instructions || '',
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setForm(emptyPrize);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.updatePrize(editing, form);
      } else {
        await api.createPrize(campaignId, form);
      }
      setShowForm(false);
      loadPrizes();
    } catch (err) {
      alert(err.message || '儲存失敗');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('確定刪除此獎項？')) return;
    try {
      await api.deletePrize(id);
      loadPrizes();
    } catch (err) {
      alert(err.message || '刪除失敗');
    }
  };

  const handleSimulate = async (times) => {
    if (!campaignId) return;
    setSimLoading(true);
    try {
      const result = await api.simulateSpin(campaignId, times);
      setSimResult(result);
    } catch (err) {
      alert(err.message || '模擬失敗');
    } finally {
      setSimLoading(false);
    }
  };

  if (!campaignId) return <div className="admin-empty">請先選擇活動</div>;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title"><FiGift style={{ marginRight: 8, verticalAlign: 'middle' }} />獎項管理</h1>
        <button className="admin-btn-primary" onClick={handleCreate}>
          <FiPlus style={{ marginRight: 6, verticalAlign: 'middle' }} />新增獎項
        </button>
      </div>

      {/* 獎項表格 */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>排序</th>
              <th>Icon</th>
              <th>名稱</th>
              <th>顯示名稱</th>
              <th>權重</th>
              <th>理論機率</th>
              <th>庫存</th>
              <th>已發</th>
              <th>剩餘</th>
              <th>狀態</th>
              <th>永不出現</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="12" className="admin-loading">載入中...</td></tr>
            ) : prizes.length === 0 ? (
              <tr><td colSpan="12" className="admin-loading">尚無獎項</td></tr>
            ) : prizes.map(p => (
              <tr key={p.id} className={!p.enabled ? 'row-disabled' : ''}>
                <td>{p.sort_order}</td>
                <td className="td-icon">{p.icon}</td>
                <td>{p.name}</td>
                <td><strong>{p.display_name}</strong></td>
                <td>{parseFloat(p.weight)}</td>
                <td>
                  <span className="rate-badge">
                    {p.theoreticalRate != null ? `${p.theoreticalRate}%` : '-'}
                  </span>
                </td>
                <td>{p.total_stock}</td>
                <td>{p.issued_stock}</td>
                <td>
                  <span className={`stock-badge ${p.remainingStock <= 0 ? 'out' : ''}`}>
                    {p.remainingStock}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${p.enabled ? 'on' : 'off'}`}>
                    {p.enabled ? '啟用' : '停用'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${p.never_show ? 'never' : 'ok'}`}>
                    {p.never_show ? '是' : '否'}
                  </span>
                </td>
                <td className="td-actions">
                  <button className="admin-btn-sm" onClick={() => handleEdit(p)}>編輯</button>
                  <button className="admin-btn-sm danger" onClick={() => handleDelete(p.id)}>刪除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 模擬抽獎測試 */}
      <div className="admin-section">
        <h2 className="admin-section-title"><FiShuffle style={{ marginRight: 8, verticalAlign: 'middle' }} />模擬抽獎測試</h2>
        <div className="sim-actions">
          <button className="admin-btn-secondary" onClick={() => handleSimulate(1000)} disabled={simLoading}>
            模擬 1,000 次
          </button>
          <button className="admin-btn-secondary" onClick={() => handleSimulate(10000)} disabled={simLoading}>
            模擬 10,000 次
          </button>
          <button className="admin-btn-secondary" onClick={() => handleSimulate(100000)} disabled={simLoading}>
            模擬 100,000 次
          </button>
        </div>

        {simResult && (
          <div className="sim-result">
            <p className="sim-info">
              模擬次數: <strong>{simResult.totalSimulations?.toLocaleString()}</strong>
              ・總權重: <strong>{simResult.totalWeight}</strong>
            </p>
            <table className="admin-table sim-table">
              <thead>
                <tr>
                  <th>獎項</th>
                  <th>抽中次數</th>
                  <th>實際比例</th>
                  <th>理論比例</th>
                  <th>偏差</th>
                </tr>
              </thead>
              <tbody>
                {simResult.results?.map((r, i) => (
                  <tr key={i}>
                    <td>{r.name}</td>
                    <td>{r.count?.toLocaleString()}</td>
                    <td><strong>{r.actualRate}%</strong></td>
                    <td>{r.theoreticalRate}%</td>
                    <td className={Math.abs(r.actualRate - r.theoreticalRate) > 2 ? 'td-warn' : ''}>
                      {(r.actualRate - r.theoreticalRate).toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 新增/編輯獎項 Modal */}
      {showForm && (
        <div className="admin-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? '編輯獎項' : '新增獎項'}</h2>

            <div className="admin-form-grid">
              <div className="admin-form-group">
                <label>內部名稱</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="admin-form-group">
                <label>顯示名稱</label>
                <input value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
              </div>
              <div className="admin-form-group">
                <label>Icon (Emoji)</label>
                <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} />
              </div>
              <div className="admin-form-group">
                <label>顏色</label>
                <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} />
              </div>
              <div className="admin-form-group">
                <label>權重 (機率)</label>
                <input type="number" step="0.01" min="0" value={form.weight}
                  onChange={e => setForm(f => ({ ...f, weight: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="admin-form-group">
                <label>總庫存</label>
                <input type="number" min="0" value={form.totalStock}
                  onChange={e => setForm(f => ({ ...f, totalStock: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="admin-form-group">
                <label>排序</label>
                <input type="number" min="0" value={form.sortOrder}
                  onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="admin-form-group">
                <label>獎項類型</label>
                <select value={form.prizeType} onChange={e => setForm(f => ({ ...f, prizeType: e.target.value }))}>
                  <option value="physical">實體獎品</option>
                  <option value="digital">數位獎品</option>
                  <option value="coupon">折價券</option>
                  <option value="consolation">安慰獎</option>
                  <option value="none">未中獎</option>
                </select>
              </div>
            </div>

            <div className="admin-form-group full">
              <label>兌獎說明</label>
              <textarea rows="3" value={form.claimInstructions}
                onChange={e => setForm(f => ({ ...f, claimInstructions: e.target.value }))} />
            </div>

            <div className="admin-form-checks">
              <label className="admin-checkbox">
                <input type="checkbox" checked={form.enabled}
                  onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
                啟用
              </label>
              <label className="admin-checkbox">
                <input type="checkbox" checked={form.neverShow}
                  onChange={e => setForm(f => ({ ...f, neverShow: e.target.checked }))} />
                永不出現
              </label>
            </div>

            <div className="admin-modal-actions">
              <button className="admin-btn-secondary" onClick={() => setShowForm(false)}>取消</button>
              <button className="admin-btn-primary" onClick={handleSave}>儲存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPrizeManagementPage;
